/**
 * Public marketing pages — About / Contact / FAQ / Privacy / Terms / Refund
 * / Demo Request / Features / Pricing. Every page shares the SMSuitePro
 * marketing header + footer defined in LoginPage.js so the nav menu stays
 * identical everywhere.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  CheckCircle, CaretDown, Envelope, Phone, MapPin, Clock, ArrowRight,
  Users, Receipt, CurrencyInr, UsersFour, ChartBar, WhatsappLogo,
} from '@phosphor-icons/react';
import { BrandMark, MarketingHeader, MarketingFooter } from '@/pages/LoginPage';
import { useSupportPhone, telHref, WhatsAppButton } from '@/components/SupportHelpLine';
import { StepCard, UpdatesList } from '@/components/QuickStartGuide';
import { UPDATES_ENABLED } from '@/lib/whatsNew';
import { QUICK_START_STEPS } from '@/lib/quickStartSteps';

function MarketingLayout({ title, kicker, subtitle, children, testid }) {
  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]">
      <MarketingHeader />

      {/* Page hero */}
      <section className="border-b border-[#E4E4E7] bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 text-center">
          {kicker && (
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#F97316] font-bold mb-3">{kicker}</div>
          )}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'var(--font-heading)' }} data-testid={`${testid}-title`}>
            {title}
          </h1>
          {subtitle && <p className="text-base text-[#52525B] mt-4 max-w-2xl mx-auto">{subtitle}</p>}
        </div>
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid={testid}>
        {children}
      </main>

      <MarketingFooter />
    </div>
  );
}

// Wide layout — for pages that need the full grid (Features, Pricing).
function MarketingLayoutWide({ title, kicker, subtitle, children, testid }) {
  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]">
      <MarketingHeader />

      <section className="border-b border-[#E4E4E7] bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 text-center">
          {kicker && (
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#F97316] font-bold mb-3">{kicker}</div>
          )}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'var(--font-heading)' }} data-testid={`${testid}-title`}>
            {title}
          </h1>
          {subtitle && <p className="text-base text-[#52525B] mt-4 max-w-2xl mx-auto">{subtitle}</p>}
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid={testid}>
        {children}
      </main>

      <MarketingFooter />
    </div>
  );
}

// ────────────────────────────────────────────────────────────── ABOUT ──

export function AboutPage() {
  return (
    <MarketingLayout
      testid="about-page"
      kicker="About Us"
      title="Built for the businesses that actually keep India running."
      subtitle="SMSuitePro (Service Management Suite Pro) is a complete business management platform designed to simplify daily operations of service-based businesses."
    >
      <div className="prose prose-zinc max-w-none">
        <p>
          Across India, thousands of small service centres — CSC Centres, Akshaya Centres,
          Janaseva Kendras, Insurance Agents, Tax Consultants — power everyday transactions
          for millions of customers. They deserve software that respects their time,
          their language, and their internet.
        </p>
        <h3>Our Mission</h3>
        <p>
          To help businesses digitise their operations with affordable, reliable software
          that just works — even on a slow connection.
        </p>
        <h3>Our Vision</h3>
        <p>
          To become the trusted business management platform for small and medium
          businesses across the country.
        </p>
        <h3>What guides us</h3>
        <ul>
          <li><b>Simple over clever</b> — a bill you can hand over in seconds beats one you have to think about.</li>
          <li><b>Data belongs to you</b> — every tenant runs in its own isolated space with encrypted backups.</li>
          <li><b>Ship fast, stay honest</b> — we tell you what changed, what broke, and what we&apos;re fixing next.</li>
        </ul>
      </div>
      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/demo-request" className="rounded-xl bg-[#F97316] text-white px-5 py-3 text-sm font-semibold hover:bg-[#EA580C]">Request a Demo</Link>
        <Link to="/pricing" className="rounded-xl border border-[#E4E4E7] px-5 py-3 text-sm font-semibold hover:bg-[#F4F4F5]">See Pricing</Link>
      </div>
    </MarketingLayout>
  );
}

// ─────────────────────────────────────────────────────────── FEATURES ──

const FEATURE_BLOCKS = [
  {
    kicker: 'Customer Management',
    title: 'A single source of truth for every customer relationship.',
    icon: Users,
    bullets: ['Complete customer database', 'Powerful search and filters', 'Full customer history timeline', 'Secure document management'],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80',
  },
  {
    kicker: 'Billing & Invoice',
    title: 'Get paid faster with professional billing tools.',
    icon: Receipt,
    bullets: ['One-click invoice creation', 'Payment tracking & status', 'Printable digital receipts', 'Credit & due management'],
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=900&q=80',
  },
  {
    kicker: 'Finance Management',
    title: 'Understand your money at a glance, every single day.',
    icon: CurrencyInr,
    bullets: ['Cash book & bank book', 'Income tracking', 'Expense tracking', 'Profit & loss reports'],
    image: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?auto=format&fit=crop&w=900&q=80',
  },
  {
    kicker: 'Staff Management',
    title: 'Empower your team with the right access and accountability.',
    icon: UsersFour,
    bullets: ['Individual staff accounts', 'Attendance tracking', 'Salary tracking', 'Role-based permissions'],
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=80',
  },
  {
    kicker: 'Reports & Analytics',
    title: 'Turn everyday activity into decisions that grow revenue.',
    icon: ChartBar,
    bullets: ['Daily reports', 'Monthly reports', 'Business analytics dashboards', 'Exportable summaries'],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80',
  },
  {
    kicker: 'Business Tools',
    title: 'The extras that keep your operation running smoothly.',
    icon: WhatsappLogo,
    bullets: ['WhatsApp reminders', 'Smart notifications', 'Automatic cloud backup', 'Multi-user access'],
    image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=900&q=80',
  },
];

export function FeaturesPage() {
  return (
    <MarketingLayoutWide
      testid="features-page"
      kicker="Features"
      title="Every tool your business runs on — unified"
      subtitle="From the first customer to your monthly profit report, SMSuitePro handles the complete workflow of a modern service business."
    >
      <div className="space-y-16">
        {FEATURE_BLOCKS.map(({ kicker, title, icon: Icon, bullets, image }, i) => {
          const reverse = i % 2 === 1;
          return (
            <div key={kicker} data-testid={`feature-block-${kicker.toLowerCase().replace(/\s+/g, '-')}`} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${reverse ? 'lg:[&>div:first-child]:col-start-7' : ''}`}>
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EFF6FF] border border-[#DBEAFE] mb-4">
                  <Icon size={14} weight="duotone" className="text-[#1E3A8A]" />
                  <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#1E3A8A]">{kicker}</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  {title}
                </h2>
                <ul className="space-y-3">
                  {bullets.map(b => (
                    <li key={b} className="flex items-start gap-3 text-sm text-[#0A0A0A]">
                      <CheckCircle size={20} weight="fill" className="text-[#10B981] shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`lg:col-span-6 ${reverse ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <div className="rounded-2xl overflow-hidden border border-[#E4E4E7] shadow-xl shadow-blue-900/5">
                  <img src={image} alt={kicker} className="w-full h-[300px] object-cover" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-16 rounded-3xl bg-gradient-to-br from-[#1E3A8A] via-[#1E40AF] to-[#0F172A] text-white p-10 sm:p-14 text-center shadow-2xl shadow-blue-900/20">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Ready to simplify your business?</h2>
        <p className="text-base text-white/80 max-w-2xl mx-auto mb-8">Join service centres across India managing their entire operation with SMSuitePro.</p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link to="/signup" className="inline-flex items-center gap-2 bg-[#F97316] text-white hover:bg-[#EA580C] rounded-xl px-6 py-3 text-sm font-semibold shadow-lg shadow-orange-500/30">Start Free Trial <ArrowRight size={16} weight="bold" /></Link>
          <Link to="/pricing" className="inline-flex items-center gap-2 rounded-xl border border-white/30 text-white px-6 py-3 text-sm font-semibold hover:bg-white/10">View Pricing</Link>
        </div>
      </div>
    </MarketingLayoutWide>
  );
}

// ──────────────────────────────────────────────────────────── PRICING ──

export function PricingPage() {
  const [trialDays, setTrialDays] = useState(15);
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('month');

  useEffect(() => {
    let cancelled = false;
    api.get('/marketing/config')
      .then(r => {
        if (cancelled) return;
        setTrialDays(r.data?.trial_days ?? 15);
        setPlans(r.data?.plans || []);
      })
      .catch(() => { /* fall back to default */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <MarketingLayoutWide
      testid="pricing-page"
      kicker="Pricing"
      title="Transparent pricing for every stage"
      subtitle={`Choose the plan that fits your centre today. Upgrade any time. Start free for ${trialDays} days — no credit card required.`}
    >
      {/* Monthly / Yearly toggle */}
      <div className="flex justify-center mb-10">
        <div className="inline-flex items-center gap-1 bg-[#F4F4F5] rounded-full p-1 text-sm font-semibold" data-testid="pricing-billing-toggle">
          <button
            type="button"
            onClick={() => setBillingCycle('month')}
            className={`px-5 py-2 rounded-full transition-colors ${billingCycle === 'month' ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-[#71717A] hover:text-[#0A0A0A]'}`}
            data-testid="pricing-toggle-monthly"
          >Monthly</button>
          <button
            type="button"
            onClick={() => setBillingCycle('year')}
            className={`px-5 py-2 rounded-full transition-colors flex items-center gap-2 ${billingCycle === 'year' ? 'bg-white text-[#0A0A0A] shadow-sm' : 'text-[#71717A] hover:text-[#0A0A0A]'}`}
            data-testid="pricing-toggle-yearly"
          >
            Yearly
            {(() => {
              const best = plans.reduce((m, p) => {
                if (!p.yearly_price || !p.price) return m;
                const pct = Math.round(100 - (p.yearly_price / (p.price * 12)) * 100);
                return Math.max(m, pct);
              }, 0);
              return best > 0 ? (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#10B981] text-white">Save {best}%</span>
              ) : null;
            })()}
          </button>
        </div>
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${plans.length >= 3 ? 'lg:grid-cols-3' : ''} gap-6 max-w-5xl mx-auto`}>
        {plans.map((p, i) => {
          const isHighlight = plans.length >= 2 && i === 1;
          const monthlyEq = p.price * 12;
          const yearlyPrice = p.yearly_price || monthlyEq;
          const savePct = p.yearly_price && p.price ? Math.round(100 - (p.yearly_price / monthlyEq) * 100) : 0;
          const displayPrice = billingCycle === 'year' ? yearlyPrice : p.price;
          const displayPeriod = billingCycle === 'year' ? 'year' : (p.period || 'month');
          const displayMrp = billingCycle === 'year' && p.mrp ? p.mrp * 12 : p.mrp;
          return (
            <div
              key={p.id}
              data-testid={`pricing-card-${p.id}`}
              className={`relative rounded-2xl border p-6 flex flex-col transition-all duration-300 ${isHighlight ? 'border-[#1E3A8A] shadow-xl shadow-blue-900/10 bg-gradient-to-b from-white to-[#EFF6FF]/40 scale-[1.02]' : 'border-[#E4E4E7] bg-white hover:border-[#1E3A8A]/50 hover:shadow-lg'}`}
            >
              {isHighlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#F97316] text-white text-[10px] uppercase tracking-wider font-bold shadow-lg shadow-orange-500/20">
                  Most Popular
                </div>
              )}
              <div className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] font-bold mb-1">Plan</div>
              <div className="text-xl font-bold text-[#0A0A0A] mb-3" style={{ fontFamily: 'var(--font-heading)' }}>{p.name}</div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-4xl font-extrabold text-[#0A0A0A]" style={{ fontFamily: 'var(--font-heading)' }} data-testid={`pricing-price-${p.id}`}>₹{Number(displayPrice || 0).toLocaleString('en-IN')}</span>
                <span className="text-sm text-[#71717A]">/{displayPeriod}</span>
              </div>
              {billingCycle === 'year' && savePct > 0 ? (
                <div className="text-xs mb-4">
                  <span className="text-[#71717A] line-through">₹{monthlyEq.toLocaleString('en-IN')}</span>
                  <span className="ml-2 text-[#059669] font-semibold">Save {savePct}% · billed annually</span>
                </div>
              ) : displayMrp && displayMrp !== displayPrice ? (
                <div className="text-xs text-[#71717A] mb-4">
                  <span className="line-through">MRP ₹{displayMrp.toLocaleString('en-IN')}</span>
                  <span className="ml-2 text-[#059669] font-semibold">Save {Math.round(100 - (displayPrice / displayMrp) * 100)}%</span>
                </div>
              ) : <div className="mb-4" />}
              <ul className="space-y-2 mb-6 flex-1">
                {(p.features || []).map((f, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-[#0A0A0A]">
                    <CheckCircle size={16} weight="fill" className="text-[#10B981] mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/signup"
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition-colors ${isHighlight ? 'bg-[#F97316] text-white hover:bg-[#EA580C]' : 'bg-[#0A0A0A] text-white hover:bg-[#27272A]'}`}
                data-testid={`pricing-cta-${p.id}`}
              >Subscribe Now <ArrowRight size={14} weight="bold" /></Link>
            </div>
          );
        })}
        {plans.length === 0 && (
          <div className="col-span-full text-center text-sm text-[#A1A1AA] py-8">Loading plans…</div>
        )}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-[#52525B]">
        <span className="inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-[#10B981]" /> {trialDays}-day free trial</span>
        <span className="inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-[#10B981]" /> No credit card required</span>
        <span className="inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-[#10B981]" /> Cancel anytime</span>
        <span className="inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-[#10B981]" /> Free onboarding</span>
      </div>

      <div className="mt-16 text-center">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#F97316] font-bold mb-3">Questions</div>
        <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Pricing FAQs</h3>
        <Link to="/faq" className="inline-flex items-center gap-1 text-sm font-semibold text-[#1E3A8A] hover:text-[#1E40AF]">See all FAQs <ArrowRight size={14} weight="bold" /></Link>
      </div>
    </MarketingLayoutWide>
  );
}

// ──────────────────────────────────────────────────────────── CONTACT ──

export function ContactPage() {
  const supportPhone = useSupportPhone();
  const [form, setForm] = useState({ name: '', mobile: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    setSubmitting(true);
    try {
      await api.post('/marketing/contact', { ...form, type: 'contact' }).catch(() => null);
      toast.success('Thanks! We\u2019ll reach out within one business day.');
      setForm({ name: '', mobile: '', email: '', message: '' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingLayout
      testid="contact-page"
      kicker="Get in Touch"
      title="We&apos;d love to hear from you."
      subtitle="Questions, feedback, partnerships — pick your channel and we&apos;ll get back within one business day."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <div className="rounded-xl border border-[#E4E4E7] p-4 flex items-start gap-3">
          <Envelope size={20} className="text-[#1E3A8A] mt-0.5" weight="duotone" />
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#71717A]">Email</div>
            <a
              href="mailto:smsuitepro@gmail.com"
              className="text-sm font-semibold text-[#0A0A0A] hover:text-[#1E3A8A]"
              data-testid="contact-email-link"
            >smsuitepro@gmail.com</a>
          </div>
        </div>
        <div className="rounded-xl border border-[#E4E4E7] p-4 flex items-start gap-3">
          <Phone size={20} className="text-[#1E3A8A] mt-0.5" weight="duotone" />
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#71717A]">Call / WhatsApp</div>
            <a
              href={telHref(supportPhone)}
              className="text-sm font-semibold text-[#0A0A0A] hover:text-[#1E3A8A]"
              data-testid="contact-phone-link"
            >{supportPhone}</a>
            <div className="text-[11px] text-[#71717A]">Mon – Sat · 9 AM – 6 PM IST</div>
            <WhatsAppButton phone={supportPhone} className="mt-2" testid="contact-whatsapp-btn" />
          </div>
        </div>
        <div className="rounded-xl border border-[#E4E4E7] p-4 flex items-start gap-3">
          <MapPin size={20} className="text-[#1E3A8A] mt-0.5" weight="duotone" />
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#71717A]">Web</div>
            <div className="text-sm font-semibold text-[#0A0A0A]">smsuitepro.com</div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4 rounded-2xl border border-[#E4E4E7] p-6 bg-[#FAFAFA]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="contact-name" /></div>
          <div><Label>Mobile</Label><Input value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} data-testid="contact-mobile" /></div>
        </div>
        <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required data-testid="contact-email" /></div>
        <div>
          <Label>Message</Label>
          <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={5} className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm" data-testid="contact-message" />
        </div>
        <Button type="submit" disabled={submitting} className="bg-[#F97316] text-white hover:bg-[#EA580C] w-full sm:w-auto" data-testid="contact-submit">
          {submitting ? 'Sending…' : 'Send Message'}
        </Button>
      </form>
    </MarketingLayout>
  );
}

// ──────────────────────────────────────────────────────────────── FAQ ──

const FAQ = [
  { q: 'What is SMSuitePro?', a: 'SMSuitePro (Service Management Suite Pro) is an all-in-one business management platform built for service-based businesses like CSC Centres, Akshaya Centres, Janaseva Kendras, insurance agents and consultants. It handles customers, billing, finance, follow-ups and staff in one place.' },
  { q: 'Who can use SMSuitePro?', a: 'Any service business that bills customers, tracks follow-ups and manages a small team. If you run a counter and hand over a receipt, SMSuitePro fits.' },
  { q: 'Is there a free trial?', a: 'Yes — every plan comes with a free trial (the current length is shown on the Pricing page). No credit card required.' },
  { q: 'How are subscription plans managed?', a: 'Plans are chosen at signup and can be changed any time from your billing settings. Pricing is displayed live on the Pricing page and stays in sync with what your account is billed.' },
  { q: 'Is my data secure?', a: 'Yes. Every tenant runs in its own isolated multi-tenant database space. Data is encrypted in transit and at rest, with daily backups.' },
  { q: 'Can I upgrade my plan?', a: 'Yes, upgrade or downgrade any time from the account settings. Changes take effect on the next billing cycle.' },
  { q: 'How can I contact support?', a: 'Email smsuitepro@gmail.com or use the Contact form on this site. We reply within one business day (Mon–Sat, 9 AM – 6 PM IST).' },
];

export function FAQPage() {
  const [open, setOpen] = useState(0);
  return (
    <MarketingLayout
      testid="faq-page"
      kicker="Frequently Asked"
      title="Answers to the questions we hear most."
      subtitle="Can&apos;t find what you need? Drop us a line via the Contact page — we usually reply the same day."
    >
      <div className="space-y-3">
        {FAQ.map((item, i) => (
          <div key={i} className={`rounded-xl border transition-colors ${open === i ? 'border-[#1E3A8A] bg-blue-50/30' : 'border-[#E4E4E7] bg-white'}`}>
            <button
              type="button"
              onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
              data-testid={`faq-toggle-${i}`}
            >
              <span className="font-semibold text-[#0A0A0A]">{item.q}</span>
              <CaretDown size={16} className={`shrink-0 transition-transform ${open === i ? 'rotate-180 text-[#1E3A8A]' : 'text-[#71717A]'}`} />
            </button>
            {open === i && (
              <div className="px-5 pb-4 text-sm text-[#52525B] leading-relaxed" data-testid={`faq-answer-${i}`}>
                {item.a}
              </div>
            )}
          </div>
        ))}
      </div>
    </MarketingLayout>
  );
}

// ────────────────────────────────────────────────────────────── LEGAL ──

function LegalPage({ testid, kicker, title, subtitle, sections }) {
  return (
    <MarketingLayout testid={testid} kicker={kicker} title={title} subtitle={subtitle}>
      <div className="prose prose-zinc max-w-none">
        <p className="text-xs text-[#71717A]">Last updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        {sections.map((s, i) => (
          <div key={i}>
            <h3>{s.heading}</h3>
            {s.body.split('\n\n').map((para, j) => <p key={j}>{para}</p>)}
          </div>
        ))}
      </div>
    </MarketingLayout>
  );
}

export function PrivacyPage() {
  return (
    <LegalPage
      testid="privacy-page"
      kicker="Legal"
      title="Privacy Policy"
      subtitle="How we collect, use and protect the information you entrust to SMSuitePro."
      sections={[
        { heading: '1. Information we collect', body: 'When you sign up we collect your business name, contact email, mobile number and payment metadata. When you use the platform, we store the transactional data your business enters — customers, invoices, payments — in your own isolated tenant space.\n\nWe do not sell your data. We do not use your business data to train AI models.' },
        { heading: '2. How we use it', body: 'We use your data to operate the service, send billing notifications, respond to support requests, and detect abuse.\n\nAggregated, anonymised usage metrics (e.g. average invoices per day) may be used to improve the product; no identifying details are shared.' },
        { heading: '3. Storage & security', body: 'All data is encrypted in transit (TLS) and at rest. Daily automated backups are retained for 30 days. Multi-tenant isolation is enforced at the query layer — one tenant can never see another tenant\u2019s rows.' },
        { heading: '4. Sharing', body: 'We share information only with sub-processors strictly needed to run the service (cloud hosting, email delivery, payment processors). We never sell it.' },
        { heading: '5. Your rights', body: 'You can export or delete your data at any time via the account settings. Contact smsuitepro@gmail.com for assisted export or account closure.' },
        { heading: '6. Changes', body: 'We\u2019ll update this policy as the service evolves and email account owners when material changes take effect.' },
      ]}
    />
  );
}

export function TermsPage() {
  return (
    <LegalPage
      testid="terms-page"
      kicker="Legal"
      title="Terms & Conditions"
      subtitle="The rules that govern your use of SMSuitePro. Plain-English, no gotchas."
      sections={[
        { heading: '1. Your account', body: 'You are responsible for the accuracy of the information you provide at signup and for keeping your login credentials safe. You must be authorised to conduct business under the account name you register.' },
        { heading: '2. Acceptable use', body: 'Don\u2019t use SMSuitePro to break the law, spam customers, or attempt to circumvent multi-tenant isolation. Abuse leads to suspension.' },
        { heading: '3. Billing', body: 'Subscription fees are billed per the plan and cycle you pick (monthly or yearly). Fees are due upfront and non-refundable except as described in the Refund Policy.\n\nWe may adjust pricing with 30 days\u2019 notice; existing tenants keep the old price until their next renewal.' },
        { heading: '4. Uptime', body: 'We target 99.5% monthly uptime excluding scheduled maintenance windows. Scheduled maintenance is announced 48 hours in advance.' },
        { heading: '5. Termination', body: 'Either party may terminate the agreement with 30 days\u2019 notice. Your data remains exportable for 30 days after termination.' },
        { heading: '6. Governing law', body: 'These terms are governed by the laws of India. Disputes are subject to the exclusive jurisdiction of the courts of Bangalore.' },
      ]}
    />
  );
}

export function RefundPage() {
  return (
    <LegalPage
      testid="refund-page"
      kicker="Legal"
      title="Refund Policy"
      subtitle="What happens if the service doesn\u2019t work out \u2014 fair and predictable."
      sections={[
        { heading: '1. Free trial', body: 'Every plan includes a free trial. No payment is collected during the trial — cancel any time before the trial ends and you owe nothing.' },
        { heading: '2. Monthly plans', body: 'Monthly subscriptions are non-refundable once billed. You can cancel any time to prevent the next month\u2019s charge; your account stays active until the end of the current period.' },
        { heading: '3. Yearly plans', body: 'Yearly subscriptions are refundable on a pro-rata basis within the first 30 days. After 30 days, cancellations stop future renewals but the current year\u2019s payment is not refunded.' },
        { heading: '4. Service issues', body: 'If we cause a prolonged outage (>24 hours in a calendar month), we credit your account with equivalent days at your request.' },
        { heading: '5. How to request', body: 'Email smsuitepro@gmail.com with your business name and a short description. We respond within one business day and process eligible refunds within 7 working days.' },
      ]}
    />
  );
}

// ─────────────────────────────────────────────────────── DEMO REQUEST ──

export function DemoRequestPage() {
  const [form, setForm] = useState({ name: '', business_name: '', mobile: '', email: '', business_type: '', preferred_date: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile || !form.email) {
      toast.error('Name, mobile and email are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/marketing/contact', { ...form, type: 'demo' }).catch(() => null);
      setDone(true);
      toast.success('Demo requested — we\u2019ll be in touch shortly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <MarketingLayout
        testid="demo-request-page"
        kicker="Thank You"
        title="Your demo is on the way."
        subtitle="Our team will reach out within one business day to schedule a walkthrough."
      >
        <div className="rounded-xl border border-[#10B981]/30 bg-[#ECFDF5] p-6 text-center">
          <CheckCircle size={40} className="text-[#10B981] mx-auto mb-3" weight="fill" />
          <p className="text-sm text-[#065F46]">
            We&apos;ve received your request and will contact you at
            {' '}<b>{form.email}</b> or <b>{form.mobile}</b> to confirm a demo slot.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/" className="rounded-xl bg-[#0A0A0A] text-white px-5 py-3 text-sm font-semibold hover:bg-[#27272A]">Back to Home</Link>
            <Link to="/signup" className="rounded-xl border border-[#0A0A0A]/20 px-5 py-3 text-sm font-semibold hover:bg-[#F4F4F5]">Start Free Trial</Link>
          </div>
        </div>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout
      testid="demo-request-page"
      kicker="Book a Demo"
      title="See SMSuitePro in action."
      subtitle="Tell us a bit about your business and we&apos;ll walk you through the exact workflow you&apos;ll run every day."
    >
      <form onSubmit={submit} className="space-y-4 rounded-2xl border border-[#E4E4E7] p-6 bg-[#FAFAFA]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="demo-name" /></div>
          <div><Label>Business Name</Label><Input value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} data-testid="demo-business" /></div>
          <div><Label>Mobile *</Label><Input value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} required data-testid="demo-mobile" /></div>
          <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required data-testid="demo-email" /></div>
          <div>
            <Label>Business Type</Label>
            <select value={form.business_type} onChange={e => setForm({ ...form, business_type: e.target.value })} className="h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm" data-testid="demo-type">
              <option value="">Select…</option>
              <option>CSC Centre</option>
              <option>Akshaya Centre</option>
              <option>Janaseva Kendra</option>
              <option>Insurance Agent</option>
              <option>Tax Consultant</option>
              <option>Small Business</option>
              <option>Other</option>
            </select>
          </div>
          <div><Label>Preferred Date</Label><Input type="date" value={form.preferred_date} onChange={e => setForm({ ...form, preferred_date: e.target.value })} data-testid="demo-date" /></div>
        </div>
        <div>
          <Label>Message</Label>
          <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={4} placeholder="Anything specific you want us to focus on?" className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm" data-testid="demo-message" />
        </div>
        <div className="flex items-center gap-3 text-xs text-[#71717A]">
          <Clock size={14} weight="duotone" className="text-[#1E3A8A]" />
          Our team typically responds within 4 business hours.
        </div>
        <Button type="submit" disabled={submitting} className="bg-[#F97316] text-white hover:bg-[#EA580C] w-full sm:w-auto" data-testid="demo-submit">
          {submitting ? 'Sending…' : 'Request Demo'}
        </Button>
      </form>
    </MarketingLayout>
  );
}

// ────────────────────────────────────────────────────────── QUICK START ──
export function QuickStartGuidePage() {
  const phone = useSupportPhone();
  return (
    <MarketingLayout
      testid="quick-start-page" kicker="Quick Start Guide"
      title="Get started in 14 simple steps"
      subtitle="Follow these steps in order — set up your company, add accounts and masters, then start billing, follow-ups and reports."
    >
      <div className="mb-10 flex flex-wrap gap-2" data-testid="qs-index">
        {UPDATES_ENABLED && <a href="#new-updates" className="rounded-full bg-[#FFF7ED] border border-[#FED7AA] px-3 py-1 text-xs font-bold text-[#C2410C]" data-testid="qs-index-updates">New Updates</a>}
        {QUICK_START_STEPS.map((s, i) => (
          <a key={s.id} href={`#qs-${s.id}`} className="rounded-full border border-[#E4E4E7] px-3 py-1 text-xs font-semibold text-[#3F3F46] hover:border-[#1E3A8A] hover:text-[#1E3A8A] transition-colors">
            {i + 1}. {s.title}
          </a>
        ))}
      </div>
      <ol className="relative border-l-2 border-dashed border-[#E4E4E7] ml-3 space-y-8">
        {QUICK_START_STEPS.map((s, i) => (
          <li key={s.id} id={`qs-${s.id}`} className="relative pl-8 scroll-mt-24">
            <span className="absolute -left-[13px] top-2 w-6 h-6 rounded-full bg-[#0F172A] text-white text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
            <div className="rounded-2xl border border-[#E4E4E7] bg-white p-6 hover:shadow-md transition-shadow">
              <StepCard step={s} index={i} />
            </div>
          </li>
        ))}
      </ol>
      {UPDATES_ENABLED && <section id="new-updates" className="mt-14 scroll-mt-24" data-testid="qs-new-updates">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#F97316] font-bold mb-2">What&apos;s new</div>
        <h2 className="text-2xl font-bold tracking-tight mb-5" style={{ fontFamily: 'var(--font-heading)' }}>New Updates</h2>
        <UpdatesList />
      </section>}
      <div className="mt-12 rounded-2xl bg-[#0F172A] text-white p-8 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="qs-assist">
        <div className="flex-1">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Need help at any step?</h3>
          <p className="text-sm text-white/70 mt-1">Call or WhatsApp our support team — we will guide you through setup.</p>
        </div>
        <a href={telHref(phone)} className="inline-flex items-center gap-2 rounded-full bg-white text-[#0F172A] px-4 py-2 text-sm font-bold" data-testid="qs-assist-phone">
          <Phone size={16} weight="fill" /> {phone}
        </a>
        <WhatsAppButton phone={phone} testid="qs-assist-whatsapp" className="!text-sm !px-4 !py-2" />
        <Link to="/signup" className="inline-flex items-center gap-1 rounded-full bg-[#F97316] px-4 py-2 text-sm font-bold" data-testid="qs-start-trial">
          Start Free Trial <ArrowRight size={14} />
        </Link>
      </div>
    </MarketingLayout>
  );
}
